import * as PIXI from 'pixi.js';
import posthog from 'posthog-js';

// ── Scene self-healing ──
// On iPhones the shared WebGL canvas can lose its context under GPU-memory
// pressure (a long interior session loads many big furniture textures), and a
// texture whose decoded image was already released cannot be re-uploaded — the
// map then comes back as bare ocean: Graphics still draw, textured sprites don't.
// Instead of leaving the player there, the current scene (map or room) is rebuilt
// from scratch — the same unmount/mount path as walking into a room and back.

export const SCENE_REBUILD_EVENT = 'scene:rebuild';
const MIN_REBUILD_GAP_MS = 20_000;
const MAX_REBUILDS_PER_SESSION = 3;
let lastRebuildAt = 0;
let rebuilds = 0;

/** Ask App to remount the current scene (throttled, logged). */
export function requestSceneRebuild(reason: string, details: Record<string, unknown> = {}): void {
  const now = Date.now();
  if (rebuilds >= MAX_REBUILDS_PER_SESSION || now - lastRebuildAt < MIN_REBUILD_GAP_MS) return;
  lastRebuildAt = now;
  rebuilds += 1;
  console.warn('[sceneHealth] rebuilding the scene:', reason, details);
  try { posthog.capture('scene_rebuilt', { reason, ...details }); } catch { /* never block the game */ }
  window.dispatchEvent(new CustomEvent(SCENE_REBUILD_EVENT, { detail: { reason } }));
}

/** A sprite texture that can no longer be drawn (released image / destroyed source). */
function isDeadTexture(texture: PIXI.Texture): boolean {
  if (texture.destroyed) return true;
  const source = texture.source;
  if (!source || source.destroyed) return true;
  // A closed ImageBitmap reports 0×0. (No resource at all = a render texture drawn
  // from Graphics — those are fine.)
  const resource = source.resource as { width?: number; height?: number } | null | undefined;
  if (!resource) return false;
  return resource.width === 0 || resource.height === 0;
}

/** Count textured sprites under `root` whose texture can no longer be drawn. */
export function countDeadTextures(root: PIXI.Container | null | undefined): number {
  if (!root || root.destroyed) return 0;
  let dead = 0;
  const walk = (node: PIXI.Container) => {
    for (const child of node.children) {
      if (child instanceof PIXI.Sprite && child.texture && child.texture !== PIXI.Texture.EMPTY && child.texture !== PIXI.Texture.WHITE) {
        if (isDeadTexture(child.texture)) dead += 1;
      }
      if (child.children.length > 0) walk(child as PIXI.Container);
    }
  };
  walk(root);
  return dead;
}
